import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const experienceData = [
  {
    id: 1,
    company: "Paramount Strategies Pte Ltd",
    period: "2023 – 2026",
    location: "Singapore",
    role: "Management Executive",
    bullets: [
      "Led corporate strategy execution and internal project tracking to ensure operational efficiency and goal alignment.",
      "Partnered with cross-functional teams to streamline business processes, improve collaboration, and enhance overall performance.",
      "Managed stakeholder communication, ensured compliance with internal policies, and supported strategic planning through performance analysis.",
      "Oversaw company IT systems, improving reliability and reducing operational downtime.",
    ],
  },
  {
    id: 2,
    company: "Easi Consultancy Pte Ltd",
    period: "2019 – 2022",
    location: "Singapore",
    role: "Management Executive",
    bullets: [
      "Managed administrative and operational functions, improving workflow efficiency and reducing task turnaround time.",
      "Acted as a key liaison between departments, streamlining internal communication and coordination.",
      "Prepared business documentation, proposals, and reports for leadership, while maintaining strong client relationships through high-quality service and support.",
      "Managed and maintained internal IT systems to support daily operations.",
    ],
  },
  {
    id: 3,
    company: "Icon Solution Pte Ltd",
    period: "2017 – 2019",
    location: "Singapore",
    role: "Management Trainee",
    bullets: [
      "Rotated across multiple business departments to support daily operations and process execution.",
      "Assisted in project coordination, administrative tasks, and business documentation while gaining exposure to corporate workflows and reporting.",
      "Contributed to internal communications, staff coordination, and workflow optimization initiatives to improve operational efficiency.",
    ],
  },
  {
    id: 4,
    company: "FitPair",
    period: "2024 – Present",
    location: "Founder",
    role: "Founder & Product Lead",
    bullets: [
      "Founded and developed a fitness mobile application focused on user engagement through matchmaking, challenges, and a token-based rewards system.",
      "Led product strategy, user experience design, and feature development from concept to launch.",
      "Managed vendor partnerships, onboarding over 40+ partners, and oversaw platform growth, testing, and deployment.",
    ],
    highlight: true,
  },
];

const ExperienceSection = () => {
  const [openId, setOpenId] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(sectionRef.current, {
        filter: "blur(8px)",
        opacity: 0.6,
        scale: 0.98,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "center top",
          end: "bottom top",
          scrub: true,
        },
      });

      gsap.from(".experience-item", {
        y: 20,
        opacity: 0,
        duration: 0.6,
        stagger: 0.1,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 75%",
          end: "bottom 90%",
          toggleActions: "play none none reverse",
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section 
      ref={sectionRef} 
      id="experience" 
      className="relative min-h-screen w-full bg-[#050505] py-20 px-6 md:px-12 overflow-hidden will-change-transform"
    >
      <div className="max-w-5xl mx-auto relative z-10">
        <h2 className="text-4xl md:text-6xl font-light text-white mb-16 tracking-tight">
          Experience
        </h2>

        <div className="flex flex-col gap-0">
          {experienceData.map((item) => {
            const isOpen = openId === item.id;
            return (
              <div key={item.id} className="experience-item border-t border-white/10 last:border-b">
                <button 
                  className="w-full text-left py-7 flex flex-col md:flex-row md:items-center gap-3 md:gap-8 group focus:outline-none"
                  onClick={() => setOpenId(isOpen ? null : item.id)}
                >
                  <span className="text-xs font-mono text-gray-500 md:w-36 shrink-0 tracking-widest">
                    {item.period}
                  </span>
                  
                  <div className="flex-1 flex flex-col md:flex-row md:items-baseline gap-1 md:gap-4">
                    <span className={`text-lg md:text-xl font-medium tracking-tight transition-colors duration-300 ${item.highlight ? 'text-white' : 'text-gray-200 group-hover:text-white'}`}>
                      {item.company}
                      {item.highlight && (
                        <span className="ml-3 text-xs font-mono text-gray-500 border border-white/10 px-2 py-0.5 rounded-full align-middle">
                          Founder
                        </span>
                      )}
                    </span>
                    <span className="text-sm text-gray-500 font-light">
                      {item.role} · {item.location}
                    </span>
                  </div>

                  <span className={`text-gray-500 text-xl transition-transform duration-300 shrink-0 ${isOpen ? 'rotate-45' : 'rotate-0'}`}>
                    +
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                      className="overflow-hidden"
                    >
                      <ul className="pb-8 pl-0 md:pl-44 flex flex-col gap-3">
                        {item.bullets.map((point, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <span className="mt-2 w-1 h-1 rounded-full bg-gray-500 shrink-0" />
                            <p className="text-gray-400 font-light leading-relaxed text-sm md:text-base">
                              {point}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;